/**
 * <hero-video-media>
 *
 * Picks a single video source based on the viewport and assigns it at runtime.
 *
 * The theme's own <video-background-component> assigns `src` to every source it
 * finds, so rendering a desktop and a mobile element and hiding one with CSS
 * would download both files. This element holds both URLs as data attributes
 * and only ever assigns the one that matches, so exactly one video is fetched.
 *
 * Re-evaluates on breakpoint change (window resize, device rotation) and on
 * prefers-reduced-motion change, and never re-requests a source it already has.
 */

const DEFAULT_BREAKPOINT = 750;

class HeroVideoMedia extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    const breakpoint = parseInt(this.dataset.breakpoint, 10) || DEFAULT_BREAKPOINT;

    this.mobileQuery = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onChange = () => this.update();

    // One stable reference, so the listeners added in update() can always be
    // removed again. 'playing' never fires when autoplay is refused, so a
    // {once:true} listener can outlive the load that registered it.
    this.reveal = () => {
      this.video.dataset.ready = 'true';
    };

    this.mobileQuery.addEventListener('change', this.onChange);
    this.motionQuery.addEventListener('change', this.onChange);

    // Belt and braces: some environments resize the viewport without firing a
    // matchMedia change event. update() early-returns when nothing changed, so
    // this costs nothing on an ordinary resize.
    this.onResize = () => {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.update());
    };
    window.addEventListener('resize', this.onResize, { passive: true });

    this.update();
  }

  disconnectedCallback() {
    this.mobileQuery?.removeEventListener('change', this.onChange);
    this.motionQuery?.removeEventListener('change', this.onChange);
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.resizeFrame);
    this.clearRevealListeners();
  }

  clearRevealListeners() {
    if (!this.video || !this.reveal) return;
    this.video.removeEventListener('playing', this.reveal);
    this.video.removeEventListener('canplay', this.reveal);
  }

  /** The source that matches the current viewport, falling back to desktop. */
  get activeSource() {
    const { desktopSrc = '', mobileSrc = '' } = this.dataset;
    return this.mobileQuery.matches && mobileSrc ? mobileSrc : desktopSrc;
  }

  update() {
    // Reduced motion: drop the source entirely and let the poster stand in.
    if (this.motionQuery.matches) {
      this.clearRevealListeners();
      this.loadedSource = null;
      this.video.removeAttribute('src');
      this.video.removeAttribute('data-ready');
      this.video.load();
      return;
    }

    const source = this.activeSource;
    if (!source || source === this.loadedSource) return;

    // Drop any listeners left over from a previous source before adding more,
    // otherwise every breakpoint crossing accumulates another pair.
    this.clearRevealListeners();

    this.loadedSource = source;
    this.video.removeAttribute('data-ready');
    this.video.src = source;
    this.video.load();

    // Reveal once the video can actually paint a frame. Listening for 'canplay'
    // as well as 'playing' means a video whose autoplay was refused shows its
    // first frame rather than sitting invisible behind the poster forever.
    this.video.addEventListener('playing', this.reveal, { once: true });
    this.video.addEventListener('canplay', this.reveal, { once: true });

    this.video.play().catch(() => {
      /* autoplay refused - the autoplay attribute lets the browser retry */
    });
  }
}

if (!customElements.get('hero-video-media')) {
  customElements.define('hero-video-media', HeroVideoMedia);
}
